/**
 * @file panels.ts
 * @description Разделы «Весь сайт» конструктора и строка «Где действует»
 * (phase19.16 Р1-6а; uiux-builder-shell.md §2.1 таблица, §3.2, §9).
 *
 * Реестр (OCP): раздел = запись — название, пояснение строкой списка,
 * значок, часть черновика (права и счётчик изменений), где действует
 * правка, нужен ли рядом сайт. Новый раздел — строка здесь, а не ветка в
 * компонентах.
 *
 * SB120 этап 4 (uiux-cart §3): раздел «Вид корзины» — на экране «Корзина»,
 * в списке «Весь сайт» его нет; без права THEME — только просмотр полей
 * (`readable`), а не объяснение вместо панели.
 */

import type { SiteViewPart } from "@chaiberi/shared";
import { Cookie, History, LayoutTemplate, Menu, Newspaper, Palette, Search, ShoppingBag, ShoppingCart, SlidersHorizontal, SwatchBook, type LucideIcon } from "lucide-react";

import { pluralRu } from "../plural";
import type { BuilderPanelKey, BuilderScreenKey, BuilderSel, BuilderSelection } from "./selection";

export interface BuilderPanelMeta {
  label: string;
  /** Строка под названием в списке «Весь сайт» (на телефоне). */
  hint: string;
  icon: LucideIcon;
  /** Часть черновика: право правки и ●N изменений; `null` — вид целиком. */
  part: SiteViewPart | null;
  /** Где действует правка — строка над панелью. */
  scope: (pages: number) => string;
  /**
   * Раздел без живого предпросмотра (SEO, настройки, история): с 1280px
   * раскрывается на место канвы, сайт — по кнопке «Показать сайт» (§7.16).
   */
  wide: boolean;
  /** Кто меняет раздел — для сотрудника без права. */
  forbidden: string;
  /** Без права панель видна только для просмотра (поля неактивны), а не объяснением. */
  readable?: boolean;
}

/** «Весь сайт — все 5 страниц и экраны магазина». */
export function wholeSiteScope(pages: number): string {
  return `Весь сайт — все ${pages} ${pluralRu(pages, ["страница", "страницы", "страниц"])} и экраны магазина; покупатели увидят после публикации вида`;
}

const SETTINGS_ONLY = "Меняет владелец магазина или сотрудник с правом настроек магазина.";
const CONTENT_ONLY = "Меняет сотрудник с правом на контент сайта.";

/** Где действует вид каталога (часть CATALOG, Р2а). */
export const CATALOG_SCOPE = "Каталог, все категории, «Похожие товары» и элемент «Товары»; покупатели увидят после публикации вида";

/** Где действует вид корзины (часть THEME, SB120 этап 4). */
export const CART_SCOPE = "Корзина на всех страницах сайта; покупатели увидят после публикации вида";

/** Где действуют счётчики и баннер cookie (часть COUNTERS, Р3г). */
export const COUNTERS_SCOPE = "Весь сайт — счётчики и баннер cookie на всех страницах; покупатели увидят после публикации вида";

/** Где действует шаблон страницы товара (часть PRODUCT_TEMPLATE, Р2а). */
export function productTemplateScope(appliesTo: number | null): string {
  const count = appliesTo === null ? "все товары на сайте" : `${appliesTo} ${pluralRu(appliesTo, ["товар", "товара", "товаров"])} на сайте`;
  return `Страница товара — ${count}; покупатели увидят после публикации вида`;
}

export const BUILDER_PANEL_META: Readonly<Record<Exclude<BuilderPanelKey, "pages">, BuilderPanelMeta>> = {
  appearance: { label: "Оформление", hint: "Цвета, шрифты, кнопки, фон страницы", icon: Palette, part: "THEME", scope: wholeSiteScope, wide: false, forbidden: SETTINGS_ONLY },
  designs: { label: "Дизайны", hint: "Сохранённые виды сайта — применить в один шаг", icon: SwatchBook, part: "THEME", scope: wholeSiteScope, wide: false, forbidden: SETTINGS_ONLY, readable: true },
  // v0.19.65 (SB146): название «Меню» остаётся (решение пользователя 2026-10-25), крошки — вкладка внутри.
  menu: { label: "Меню", hint: "Пункты шапки и подвала, хлебные крошки", icon: Menu, part: "MENUS", scope: () => "Шапка и подвал всех страниц, меню телефона, хлебные крошки", wide: false, forbidden: CONTENT_ONLY },
  settings: { label: "Настройки сайта", hint: "Название, логотипы, контакты, продавец, доступ к сайту", icon: SlidersHorizontal, part: "BRAND", scope: () => "Весь сайт", wide: true, forbidden: SETTINGS_ONLY },
  // Р3в (SB145): SEO не часть вида сайта — панель ведёт в раздел «Сайт → SEO» (права и ●N не нужны).
  seo: { label: "SEO", hint: "Теперь в разделе «Сайт → SEO»", icon: Search, part: null, scope: () => "Раздел «Сайт → SEO» — сохраняется сразу, без публикации", wide: false, forbidden: "" },
  counters: { label: "Счётчики и cookie", hint: "Яндекс Метрика, VK Реклама и баннер согласия на cookie", icon: Cookie, part: "COUNTERS", scope: () => COUNTERS_SCOPE, wide: false, forbidden: SETTINGS_ONLY },
  blog: { label: "Блог", hint: "Вид ленты и статьи, блоки над и под лентой, RSS", icon: Newspaper, part: "BLOG", scope: () => "Лента блога, страницы статей и лента RSS; покупатели увидят после публикации вида", wide: false, forbidden: CONTENT_ONLY },
  catalog: { label: "Каталог", hint: "Список товаров, фильтры и карточка товара", icon: ShoppingBag, part: "CATALOG", scope: () => CATALOG_SCOPE, wide: false, forbidden: CONTENT_ONLY },
  cart: { label: "Вид корзины", hint: "Окно из шапки или панель сбоку, цвета и размеры", icon: ShoppingCart, part: "THEME", scope: () => CART_SCOPE, wide: false, forbidden: SETTINGS_ONLY, readable: true },
  history: { label: "История публикаций", hint: "Прошлые версии вида сайта — посмотреть и вернуть", icon: History, part: null, scope: () => "Вид сайта целиком", wide: true, forbidden: "" },
};

/** Порядок в списке «Весь сайт». */
export const WHOLE_SITE_ORDER: readonly Exclude<BuilderPanelKey, "pages">[] = ["appearance", "designs", "menu", "settings", "seo", "counters", "blog", "catalog", "history"];

/** Значок раздела «Страницы» (переключатель страниц). */
export const PAGES_ICON: LucideIcon = LayoutTemplate;

/** Название экрана магазина в конструкторе: «Страница товара» — шаблон (Р2а), прочие — как в предпросмотре. */
export const BUILDER_SCREEN_TITLES: Readonly<Partial<Record<string, string>>> = {
  product: "Страница товара",
};

/** Подзаголовок шапки конструктора на экране магазина (Р2а: у каталога и шаблона — не «только вид»). */
export const BUILDER_SCREEN_SUBTITLES: Readonly<Partial<Record<string, string>>> = {
  catalog: "Каталог — вид списка и карточки, блоки над и под списком",
  product: "Шаблон страницы товара — один на все товары; на сайт после «Опубликовать»",
  cart: "Корзина — окно из шапки или панель сбоку; на сайт после «Опубликовать»",
};

/** Кнопка правой панели над структурой: у экранов каталога, шаблона и корзины — своя подпись (реестр). */
const SETTINGS_BUTTON_LABELS: Readonly<Partial<Record<string, string>>> = {
  catalog: "Вид каталога",
  product: "О шаблоне",
  cart: "Вид корзины",
};

export function settingsButtonLabel(screen: string | null): string {
  return (screen && SETTINGS_BUTTON_LABELS[screen]) || "Настройки страницы";
}

/**
 * Раздел, который правая панель показывает на экране магазина без явного
 * раздела (`panel` в адресе нет): каталог — вид каталога, корзина — вид
 * корзины. Новый экран со своим видом — строка здесь.
 */
export const SCREEN_PANELS: Readonly<Partial<Record<BuilderScreenKey, Exclude<BuilderPanelKey, "pages">>>> = {
  catalog: "catalog",
  cart: "cart",
};

/** Где действует выбор на странице. */
export function selectionScope(sel: BuilderSel, pageTitle: string | null, pages: number): string {
  if (sel.kind === "block") return "Только эта страница · на сайт — кнопкой «Сохранить блок»";
  if (sel.kind === "area") return wholeSiteScope(pages);
  return pageTitle ? `Только страница «${pageTitle}»` : "Только эта страница";
}

/**
 * Переход «Сайт → Каталог → Карточка товара» из панелей конструктора
 * («Товары», «Похожие товары»): один объект выбора на все ссылки.
 */
export const CATALOG_CARD_SELECTION: Readonly<Partial<BuilderSelection>> = { pageId: null, screen: "catalog", panel: "catalog", sub: "card", sel: { kind: "page" } };

/**
 * Открыт ли раздел, который экран показывает и без него (каталог на экране
 * «Каталог», вид корзины на экране «Корзина»): закрыть его ✕ — показать то
 * же самое, поэтому ✕ у такой панели нет (ревью SB120 этапа 4).
 */
export function isOwnScreenPanel(screen: BuilderScreenKey | null, panel: BuilderPanelKey | null): boolean {
  return screen !== null && panel !== null && SCREEN_PANELS[screen] === panel;
}
